/**
 * FaceSwap 智能提示词构建器
 * 与 options 文件保持完全一致的键值分类
 */

import {
  technicalParameterMappings,
  styleAndMoodMappings,
  characterMappings,
  environmentMappings
} from './promptMappings.js';

// 角色相关键值 - 对应 RoleOptions.js (18个子选项)
const subjectKeys = [
  'gender',              // 性别
  'age',                 // 年龄
  'expression',          // 表情
  'eye_color',           // 眼睛颜色
  'hair_style',          // 发型
  'hair_color',          // 发色
  'beard',               // 胡须
  'nationality',         // 国籍
  'body_weight',         // 体重
  'face_shape',          // 脸型
  'facial_feature_details', // 面部特征细节
  'eye_features',        // 眼部特征
  'ears',                // 耳朵
  'skin_details',        // 皮肤细节
  'skin_texture_effect', // 皮肤纹理效果
  'clothing_style',      // 服装风格
  'clothing_texture',    // 服装质感
  'head_accessories'     // 头部配饰
];

// 技术参数键值 - 对应 CameraOptions.js (13个子选项)
const technicalKeys = [
  'camera_type',         // 相机类型
  'lens_type',           // 镜头类型
  'focal_length',        // 焦距
  'aperture',            // 光圈
  'shutter_speed',       // 快门速度
  'iso',                 // ISO
  'white_balance',       // 白平衡
  'focus_mode',          // 对焦模式
  'exposure_mode',       // 曝光模式
  'shooting_mode',       // 拍摄模式
  'shooting_angle',      // 拍摄角度
  'pose'                 // 姿势
];

// 风格相关键值 - 对应 StyleOptions.js (8个子选项)
const styleKeys = [
  'shot_type',           // 拍摄类型
  'imaging_style',       // 成像风格
  'artistic_style',      // 艺术风格
  'art_effect',          // 艺术效果
  'depth_of_field',      // 景深
  'photo_effect',        // 照片效果
  'special_effect',      // 特殊效果
  'post_processing_style' // 后期处理风格
];

// 环境相关键值 - 对应 EnvOptions.js (6个子选项)
const environmentKeys = [
  'environment_atmosphere', // 环境氛围
  'light_style',           // 光线风格
  'light_direction',       // 光线方向
  'background',            // 背景
  'background_effect',     // 背景效果
  'background_blur'        // 背景模糊
];

/**
 * 获取映射值
 * @param {string} key - 键名
 * @param {string} value - 值
 * @returns {string} 映射后的英文提示词
 */
export function getMappedValue(key, value) {
  // 直接使用英文键值，不需要转换
  const englishKey = value;
  
  // 根据键的类型选择对应的映射表
  if (subjectKeys.includes(key)) {
    return characterMappings[key]?.[englishKey] || '';
  } else if (technicalKeys.includes(key)) {
    return technicalParameterMappings[key]?.[englishKey] || '';
  } else if (styleKeys.includes(key)) {
    return styleAndMoodMappings[key]?.[englishKey] || '';
  } else if (environmentKeys.includes(key)) {
    return environmentMappings[key]?.[englishKey] || '';
  }
  
  return '';
}

/**
 * 构建智能提示词
 * @param {Object} selectedOptions - 选中的选项
 * @param {string} userPrompt - 用户输入的提示词
 * @returns {string} 构建的完整提示词
 */
export function buildIntelligentPrompt(selectedOptions, userPrompt = '', mode = 'text-to-image') {
  const promptParts = {
    subject: [],
    technical: [],
    style: [],
    environment: []
  };

  // 定义前缀常量，避免重复
  const prefixes = {
    subject: 'Character features of the image:',
    technical: 'Photographic characteristics of images:',
    style: 'Stylistic features of images:',
    environment: 'Environmental characteristics of the image:'
  };

  // 跟踪是否已添加前缀
  const prefixAdded = {
    subject: false,
    technical: false,
    style: false,
    environment: false
  };

  // 遍历所有选中的选项
  Object.entries(selectedOptions).forEach(([key, value]) => {
    if (!value || value === 'default') return;

    const mappedValue = getMappedValue(key, value);
    if (!mappedValue) return;

    // 根据键的类型分类，只在第一次添加前缀
    if (subjectKeys.includes(key)) {
      if (!prefixAdded.subject) {
        promptParts.subject.push(prefixes.subject + mappedValue);
        prefixAdded.subject = true;
      } else {
        promptParts.subject.push(mappedValue);
      }
    } else if (technicalKeys.includes(key)) {
      if (!prefixAdded.technical) {
        promptParts.technical.push(prefixes.technical + mappedValue);
        prefixAdded.technical = true;
      } else {
        promptParts.technical.push(mappedValue);
      }
    } else if (styleKeys.includes(key)) {
      if (!prefixAdded.style) {
        promptParts.style.push(prefixes.style + mappedValue);
        prefixAdded.style = true;
      } else {
        promptParts.style.push(mappedValue);
      }
    } else if (environmentKeys.includes(key)) {
      if (!prefixAdded.environment) {
        promptParts.environment.push(prefixes.environment + mappedValue);
        prefixAdded.environment = true;
      } else {
        promptParts.environment.push(mappedValue);
      }
    }
  });

  // 构建最终提示词
  const parts = [];
  
  // 用户输入的提示词放在最前面
  if (userPrompt.trim()) {
    parts.push(userPrompt.trim());
  }
  
  // 角色特征
  if (promptParts.subject.length > 0) {
    parts.push(promptParts.subject.join(', '));
  }
  
  // 技术参数
  if (promptParts.technical.length > 0) {
    parts.push(promptParts.technical.join(', '));
  }
  
  // 风格效果
  if (promptParts.style.length > 0) {
    parts.push(promptParts.style.join(', '));
  }
  
  // 环境光照
  if (promptParts.environment.length > 0) {
    parts.push(promptParts.environment.join(', '));
  }
  const image_to_image_pre = "You are an expert photo editor. Your task is to meticulously edit the provided portrait.It is absolutely crucial to preserve the person's fundamental identity, likeness, and facial structure with the highest possible fidelity.Only apply the following specific changes, organized by category. Do not alter any features not explicitly mentioned. The prompt word is:";
  const text_to_image_pre =  "Your task is to create a single portrait based on the prompts. Be sure to preserve the person's essential identity, likeness, and facial structure with the highest possible fidelity. Apply only the specific changes listed in the prompt. Do not change any features not explicitly mentioned. The prompt is:";
  const corePrompt = parts.filter(part => part.length > 0).join(', ');
  const prefix = mode === 'image-to-image' ? image_to_image_pre : text_to_image_pre;
  return corePrompt ? `${prefix} ${corePrompt}` : prefix;
}

/**
 * 验证提示词质量
 * @param {string} prompt - 提示词
 * @returns {Object} 验证结果
 */
export function validatePromptQuality(prompt) {
  const issues = [];
  const words = prompt.toLowerCase().split(/[,\s]+/).filter(word => word.length > 0);
  
  // 检查长度
  if (prompt.length > 500) {
    issues.push('提示词过长，建议控制在500字符以内');
  }
  
  if (prompt.length < 10) {
    issues.push('提示词过短，建议添加更多描述');
  }
  
  // 检查重复词汇
  const wordCount = {};
  words.forEach(word => {
    wordCount[word] = (wordCount[word] || 0) + 1;
  });
  
  const repeatedWords = Object.entries(wordCount)
    .filter(([word, count]) => count > 2 && word.length > 3)
    .map(([word]) => word);
  
  if (repeatedWords.length > 0) {
    issues.push(`发现重复词汇: ${repeatedWords.join(', ')}`);
  }
  
  // 检查是否包含基本要素
  const hasSubject = words.some(word => 
    ['man', 'woman', 'person', 'face', 'portrait'].includes(word)
  );
  
  if (!hasSubject) {
    issues.push('建议添加人物主体描述');
  }
  
  return {
    isValid: issues.length === 0,
    issues,
    wordCount: words.length,
    charCount: prompt.length
  };
}

/**
 * 获取提示词建议
 * @param {Object} selectedOptions - 当前选中的选项
 * @returns {Array} 建议列表
 */
export function getPromptSuggestions(selectedOptions) {
  const suggestions = [];
  
  // 检查是否缺少基本要素
  const hasGender = selectedOptions.gender && selectedOptions.gender !== 'default';
  const hasAge = selectedOptions.age && selectedOptions.age !== 'default';
  const hasExpression = selectedOptions.expression && selectedOptions.expression !== 'default';
  
  if (!hasGender) {
    suggestions.push('建议选择性别以获得更准确的结果');
  }
  
  if (!hasAge) {
    suggestions.push('建议选择年龄段以增强人物特征');
  }
  
  if (!hasExpression) {
    suggestions.push('建议选择表情以增加画面生动性');
  }
  
  // 检查技术参数
  const hasCameraSettings = technicalKeys.some(key => 
    selectedOptions[key] && selectedOptions[key] !== 'default'
  );
  
  if (!hasCameraSettings) {
    suggestions.push('建议添加相机参数以提升照片质量');
  }
  
  // 检查环境设置
  const hasEnvironment = environmentKeys.some(key => 
    selectedOptions[key] && selectedOptions[key] !== 'default'
  );
  
  if (!hasEnvironment) {
    suggestions.push('建议设置环境和光照以营造氛围');
  }
  
  return suggestions;
}

export const promptBuilder = {
  buildIntelligentPrompt,
  getMappedValue,
  validatePromptQuality,
  getPromptSuggestions
};

export default promptBuilder;